/**
 * AutocompleteCombo - 입력 시 콤보 항목이 동적으로 필터되어 선택 가능한 위젯
 *
 * @param {Array} options - 선택 옵션 배열 (객체 또는 { value, label })
 * @param {Function} getOptionLabel - (item) => string
 * @param {Function} getOptionValue - (item) => any
 * @param {*} value - 단일 선택 시 선택된 값 (선택 사항)
 * @param {Function} onChange - (value, item) => void 단일 선택 시
 * @param {Function} onSelectItem - (item) => void 선택 시 콜백만 필요할 때 (다중 선택 토글 등)
 * @param {string} placeholder - 입력 placeholder
 * @param {Function} filterOption - (item, inputValue) => boolean (기본: 라벨 포함 검색)
 * @param {string} inputValue - 제어 모드 입력값 (선택)
 * @param {Function} onInputChange - 제어 모드 입력 변경 (선택)
 * @param {string} className - 컨테이너 클래스
 * @param {string} inputId - input id (접근성)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const defaultFilter = (item, inputValue, getOptionLabel) => {
  const label = getOptionLabel(item);
  return String(label || '').toLowerCase().includes(String(inputValue || '').trim().toLowerCase());
};

const AutocompleteCombo = ({
  options = [],
  getOptionLabel = (item) => (item != null && typeof item === 'object' ? item.label ?? item.title ?? item.name ?? String(item) : String(item)),
  getOptionValue = (item) => (item != null && typeof item === 'object' ? item.value ?? item.id ?? item.demandId : item),
  value = null,
  onChange,
  onSelectItem,
  placeholder,
  filterOption,
  inputValue: controlledInputValue,
  onInputChange,
  className = '',
  inputId,
  'aria-label': ariaLabel,
}) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('components.autocomplete.placeholder');
  const [internalInputValue, setInternalInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const isControlled = controlledInputValue !== undefined;
  const inputValue = isControlled ? controlledInputValue : internalInputValue;
  const setInputValue = useCallback(
    (next) => {
      if (!isControlled) setInternalInputValue(next);
      onInputChange?.(next);
    },
    [isControlled, onInputChange]
  );

  const selectedOption = useMemo(() => {
    if (value == null || value === '') return null;
    const valueStr = String(value);
    return options.find((opt) => String(getOptionValue(opt)) === valueStr) ?? null;
  }, [options, value, getOptionValue]);

  const filterFn = useCallback(
    (item, iv) => (filterOption ? filterOption(item, iv) : defaultFilter(item, iv, getOptionLabel)),
    [filterOption, getOptionLabel]
  );

  const filteredOptions = useMemo(() => {
    const k = String(inputValue || '').trim();
    if (!k) return options;
    return options.filter((opt) => filterFn(opt, inputValue));
  }, [options, inputValue, filterFn]);

  const displayLabel = selectedOption && !isOpen ? getOptionLabel(selectedOption) : inputValue;

  useEffect(() => {
    if (!isOpen) setHighlightIndex(-1);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightIndex];
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightIndex]);

  const handleFocus = useCallback(() => {
    setIsOpen(true);
    if (selectedOption) setInputValue('');
  }, [selectedOption, setInputValue]);

  const handleChange = useCallback(
    (e) => {
      const v = e.target.value;
      setInputValue(v);
      setIsOpen(true);
      setHighlightIndex(0);
    },
    [setInputValue]
  );

  const handleSelect = useCallback(
    (item) => {
      const val = getOptionValue(item);
      if (onChange) onChange(val, item);
      if (onSelectItem) onSelectItem(item);
      setInputValue('');
      setIsOpen(false);
      setHighlightIndex(-1);
    },
    [getOptionValue, onChange, onSelectItem, setInputValue]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === 'ArrowDown') {
          e.preventDefault();
          setIsOpen(true);
          setHighlightIndex(0);
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Enter' && highlightIndex >= 0 && filteredOptions[highlightIndex]) {
        e.preventDefault();
        handleSelect(filteredOptions[highlightIndex]);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setHighlightIndex(-1);
      }
    },
    [isOpen, filteredOptions, highlightIndex, handleSelect]
  );

  const showDropdown = isOpen && filteredOptions.length > 0;

  return (
    <div className={`autocomplete-combo ${className}`} ref={containerRef}>
      <input
        id={inputId}
        type="text"
        className="form-control autocomplete-combo-input"
        placeholder={resolvedPlaceholder}
        value={displayLabel}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={inputId ? `${inputId}-listbox` : undefined}
        role="combobox"
      />
      {showDropdown && (
        <ul
          ref={listRef}
          id={inputId ? `${inputId}-listbox` : undefined}
          className="autocomplete-combo-list"
          role="listbox"
        >
          {filteredOptions.map((item, idx) => (
            <li
              key={getOptionValue(item)}
              className={`autocomplete-combo-option ${highlightIndex === idx ? 'highlight' : ''}`}
              role="option"
              aria-selected={highlightIndex === idx}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(item);
              }}
              onMouseEnter={() => setHighlightIndex(idx)}
            >
              {getOptionLabel(item)}
            </li>
          ))}
        </ul>
      )}
      {isOpen && filteredOptions.length === 0 && inputValue.trim() && (
        <ul className="autocomplete-combo-list autocomplete-combo-list-empty" role="listbox">
          <li className="autocomplete-combo-empty">{t('components.autocomplete.noResults')}</li>
        </ul>
      )}
      <style>{`
        .autocomplete-combo { position: relative; width: 100%; }
        .autocomplete-combo-input { width: 100%; font-size: 14px; padding: 6px 10px; border: 1px solid #e5e7eb; border-radius: 6px; }
        .autocomplete-combo-input:focus { border-color: var(--theme-color-primary, #e97730); outline: none; box-shadow: 0 0 0 2px rgba(233,119,48,0.2); }
        .autocomplete-combo-list { position: absolute; left: 0; right: 0; top: 100%; margin: 4px 0 0; padding: 4px 0; max-height: 220px; overflow-y: auto; background: #fff; border: 1px solid #e5e7eb; border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 100; list-style: none; }
        .autocomplete-combo-option { padding: 8px 12px; font-size: 14px; cursor: pointer; color: #1f2937; }
        .autocomplete-combo-option:hover, .autocomplete-combo-option.highlight { background: #f0f9ff; color: #0c4a6e; }
        .autocomplete-combo-empty { padding: 12px; font-size: 13px; color: #94a3b8; text-align: center; }
      `}</style>
    </div>
  );
};

export default AutocompleteCombo;
